'use client'

import { useMandatoryContext } from '@repo/hooks'
import {
  createContext,
  type Dispatch,
  type PropsWithChildren,
  type SetStateAction,
  useCallback,
  useEffect,
  useMemo,
  useState
} from 'react'

import { Dialog, type DialogOptions } from '../components/client'

export type DialogContent = React.ReactNode | false

export interface ToggleDialog {
  (dialogContent: DialogContent, dialogOptions?: DialogOptions): void
}

export interface DialogContextProps {
  isOpen: boolean
  /** Content currently rendered in the dialog, `null` when closed */
  dialogContent: React.ReactNode | null
  /**
   * Toggles the dialog state
   * @param toggleDialogOptions - Component to render in the dialog and optional isOpen state
   * @example
   * toggleDialog(<Component />); // Open the dialog with the provided content
   * toggleDialog(false); // Closes the dialog state
   * toggleDialog(false); // Closes the dialog state
   * @see ToggleDialogOptions
   * @see DialogContextProvider
   */
  toggleDialog: ToggleDialog
  setDialogOptions: Dispatch<SetStateAction<DialogOptions>>
}

const DialogContext = createContext<DialogContextProps | null>(null)

/**
 * Hook to use the Dialog Context
 * @returns Dialog Context
 * @throws Error if used outside of DialogContextProvider
 * @example
 * const { isOpen, toggleDialog } = useDialogContext();
 * @see DialogContextProvider
 * @see DialogContextProps
 */
export const useDialogContext = () => {
  return useMandatoryContext(DialogContext, 'DialogContext')
}

export interface DialogProviderProps extends PropsWithChildren {}

export const DialogContextProvider = ({ children }: DialogProviderProps) => {
  const [isOpen, setIsOpen] = useState(false)
  const [dialogContent, setDialogContent] = useState<React.ReactNode | null>(null)
  const [dialogOptions, setDialogOptions] = useState<DialogOptions>({})

  const toggleDialog = useCallback(
    (dialogContent: DialogContent, dialogOptions: DialogOptions = {}) => {
      if (!dialogContent) {
        setIsOpen(false)
        setDialogContent(null)
        setDialogOptions({})
        return
      }

      if (typeof dialogContent === 'object') {
        setDialogContent(dialogContent)
        setDialogOptions(dialogOptions)
        setIsOpen(true)
        return
      }
    },
    []
  )

  const requestClose = useCallback(() => {
    if (dialogOptions.closable === false) return
    dialogOptions.onClose?.()
    toggleDialog(false)
  }, [dialogOptions, toggleDialog])

  const handlePopState = useCallback(() => {
    if (isOpen) requestClose()
  }, [isOpen, requestClose])

  // Close dialog on browser back/forward navigation, unless the dialog opted out of dismissal
  useEffect(() => {
    window.addEventListener('popstate', handlePopState)
    return () => {
      window.removeEventListener('popstate', handlePopState)
    }
  }, [handlePopState])

  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') requestClose()
    }

    document.addEventListener('keydown', handleKeyDown)
    return () => {
      document.removeEventListener('keydown', handleKeyDown)
    }
  }, [requestClose])

  const value = useMemo(
    () => ({
      isOpen,
      dialogContent,
      toggleDialog,
      setDialogOptions
    }),
    [isOpen, dialogContent, toggleDialog, setDialogOptions]
  )

  return (
    <DialogContext.Provider value={value}>
      <Dialog isOpen={isOpen} dialogOptions={dialogOptions} onClose={requestClose}>
        {dialogContent}
      </Dialog>
      {children}
    </DialogContext.Provider>
  )
}
